import { FolderOpen, ListChecks, Play, RefreshCw, Square } from 'lucide-react'
import { useState } from 'react'

import { Button, SelectField } from '@krp/ui-kit'

import { ActionError } from '../../../shared/ActionError'
import { QueryState } from '../../../shared/QueryState'
import { LANE_TITLES } from '../model/selectors'
import { TESTING_LANES, type TestingAccess, type TestingLane, type TestingRunsActions, type TestingRunsVm } from '../model/types'
import { Share, StateBadge, TestingGate } from './TestingParts'

const LANE_OPTIONS = TESTING_LANES.map((lane) => ({ value: lane, label: lane === 'all' ? 'Все дорожки и слияние' : LANE_TITLES[lane] }))

/** Прогоны замера: последний балл, история и запуск нового. */
export function TestingRunsScreen({ access, vm, actions }: { access: TestingAccess; vm: TestingRunsVm; actions: TestingRunsActions }) {
  const [lanes, setLanes] = useState<TestingLane>('all')
  return (
    <TestingGate access={access}>
      <section aria-labelledby="platform-testing-runs-title" className="platform-testing">
        <header className="platform-resources__toolbar">
          <h2 id="platform-testing-runs-title">Поиск по фото · Замеры</h2>
          <div className="platform-testing__row">
            <Button variant="ghost" icon={<ListChecks size={16} aria-hidden="true" />} onClick={actions.openProducts} actionId="platform.testing-runs.open-products">
              Тестовые товары
            </Button>
            <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="platform.testing-runs.reload">
              Обновить
            </Button>
          </div>
        </header>
        <p className="platform-testing__note">
          Каталог замера — тестовый: баллы сравнивают прогоны между собой, а не обещают такую же точность в магазине.
        </p>
        <ActionError error={vm.action.error} onReload={actions.reload} />

        {vm.latest !== null && (
          <section aria-label="Последний замер">
            <h3>{vm.latest.title}</h3>
            <dl className="platform-testing__scores">
              {vm.latest.scores.map((score) => (
                <div key={score.label}>
                  <dt>{score.label}</dt>
                  <dd>
                    <Share share={score.share} />
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        <section aria-label="Новый замер">
          <div className="platform-testing__row">
            <SelectField
              label="Что мерить"
              value={lanes}
              options={LANE_OPTIONS}
              onChange={(value) => setLanes(TESTING_LANES.find((lane) => lane === value) ?? 'all')}
              actionId="platform.testing-runs.lanes"
            />
            <Button
              variant="primary"
              icon={<Play size={16} aria-hidden="true" />}
              disabled={vm.running !== null || vm.action.busy}
              disabledReason={vm.running}
              onClick={() => actions.start(lanes)}
              actionId="platform.testing-runs.start"
            >
              Запустить замер
            </Button>
          </div>
        </section>

        <section aria-label="Прогоны">
          <h3>Прогоны</h3>
          {vm.query.status !== 'ready' ? (
            <QueryState query={vm.query} emptyText="Замеров ещё не было" onRetry={actions.reload} />
          ) : (
            <div className="platform-sync__table-wrap">
              <table className="platform-sync__table">
                <thead>
                  <tr>
                    <th>Когда</th>
                    <th>Состояние</th>
                    <th>Первое место</th>
                    <th>В пятёрке</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {vm.rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        {row.when}
                        <div className="platform-testing__note">{row.lanesText}</div>
                      </td>
                      <td>
                        <StateBadge badge={row.state} />
                        <div className="platform-testing__note">{row.progress}</div>
                      </td>
                      <td>{row.top1 === null ? '—' : <Share share={row.top1} />}</td>
                      <td>{row.top5 === null ? '—' : <Share share={row.top5} />}</td>
                      <td>
                        <div className="platform-testing__row">
                          <Button
                            variant="ghost"
                            icon={<FolderOpen size={16} aria-hidden="true" />}
                            aria-label={`Открыть прогон ${row.when}`}
                            onClick={() => actions.open(row.id)}
                            actionId="platform.testing-runs.open"
                          >
                            Открыть
                          </Button>
                          {row.cancellable && (
                            <Button
                              variant="ghost"
                              icon={<Square size={16} aria-hidden="true" />}
                              aria-label={`Отменить прогон ${row.when}`}
                              onClick={() => actions.cancel(row.id)}
                              actionId="platform.testing-runs.cancel"
                            >
                              Отменить
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </section>
    </TestingGate>
  )
}
